/**
 * Auth local (email + password) que coexiste con MSAL.
 *
 * - Cuando el usuario hace signup/login email/password, guardamos el JWT
 *   + refresh token en localStorage. `useApi()` prefiere el access sobre
 *   el de MSAL y dispara silent refresh automáticamente al recibir 401.
 * - Cuando el usuario hace logout, limpiamos ambos tokens y el user.
 * - Si no hay token local, `useApi()` cae al flujo MSAL (legacy).
 *
 * Asi no rompemos el dev con MSAL mockeado, y tenemos signup real
 * para multi-tenant.
 */
import {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { apiClient } from '../api/http';

const STORAGE_TOKEN = 'deca:auth:token';
const STORAGE_REFRESH = 'deca:auth:refresh';
const STORAGE_USER = 'deca:auth:user';

export interface LocalUser {
  id: string;
  email: string;
  name: string;
  tenantId: string;
  roles: string[];
}

export interface LoginInput {
  email: string;
  password: string;
}

/**
 * Datos fiscales del cliente que el signup publica al backend.
 *
 * Van al Tenant (container `tenants`), NO al User. El plan vive tambien
 * en el Tenant, no en el User.
 */
export interface SignupTenantInput {
  razonSocial: string;
  nifCif: string;
  /** Opcional: el autonomo lo rellena al alta o mas tarde desde Configuracion. */
  direccion?: string;
  municipio?: string;
  codigoPostal?: string;
  provincia?: string;
  telefono?: string;
  pais?: string;
}

/** Plan comercial elegido. Coincide con la landing y con el backend. */
export type PlanCode = 'gratis' | 'simple' | 'todo';

export interface SignupInput {
  email: string;
  password: string;
  name: string;
  /** Default `gratis`. La landing lo pre-selecciona via `?plan=...`. */
  plan?: PlanCode;
  tenant: SignupTenantInput;
  /** Aceptacion expresa de las Condiciones del servicio (obligatoria). */
  aceptaCondiciones: true;
  /** Version aceptada (ver src/legal/condiciones.ts). */
  condicionesVersion: string;
}

interface AuthResponse {
  user: LocalUser;
  accessToken: string;
  refreshToken: string;
  expiresIn: number;
}

export interface AuthContextValue {
  token: string | null;
  user: LocalUser | null;
  /** True si hay sesion local activa (signup o login con email/password). */
  isAuthenticated: boolean;
  signup: (input: SignupInput) => Promise<LocalUser>;
  login: (input: LoginInput) => Promise<LocalUser>;
  logout: () => void;
  /**
   * Renueva el par de tokens contra /auth/refresh y los persiste.
   * Devuelve el nuevo accessToken para que el interceptor lo inyecte
   * sin tener que esperar a un re-render. Si el refresh falla (401),
   * limpia la sesión y rechaza.
   */
  refresh: () => Promise<string>;
  /**
   * Acceso sincrónico al refresh token, para que el interceptor de
   * useApi() lo pueda leer sin esperar a React.
   */
  getRefreshToken: () => string | null;
}

const AuthContext = createContext<AuthContextValue | null>(null);

export function LocalAuthProvider({ children }: { children: ReactNode }) {
  const [token, setToken] = useState<string | null>(() => localStorage.getItem(STORAGE_TOKEN));
  const [user, setUser] = useState<LocalUser | null>(() => {
    const raw = localStorage.getItem(STORAGE_USER);
    if (!raw) return null;
    try {
      return JSON.parse(raw) as LocalUser;
    } catch {
      return null;
    }
  });
  // Ref espejo para que getRefreshToken() lea el valor actual sin esperar
  // a un re-render (importante en el flujo interceptor → refresh).
  const refreshRef = useRef<string | null>(localStorage.getItem(STORAGE_REFRESH));

  const persist = useCallback((t: string | null, r: string | null, u: LocalUser | null) => {
    if (t && r && u) {
      localStorage.setItem(STORAGE_TOKEN, t);
      localStorage.setItem(STORAGE_REFRESH, r);
      localStorage.setItem(STORAGE_USER, JSON.stringify(u));
    } else {
      localStorage.removeItem(STORAGE_TOKEN);
      localStorage.removeItem(STORAGE_REFRESH);
      localStorage.removeItem(STORAGE_USER);
    }
    refreshRef.current = r;
    setToken(t);
    setUser(u);
  }, []);

  const clearSession = useCallback(() => persist(null, null, null), [persist]);

  const signup = useCallback(
    async (input: SignupInput): Promise<LocalUser> => {
      const res = await apiClient.post<AuthResponse>('/auth/signup', input);
      persist(res.data.accessToken, res.data.refreshToken, res.data.user);
      return res.data.user;
    },
    [persist],
  );

  const login = useCallback(
    async (input: LoginInput): Promise<LocalUser> => {
      const res = await apiClient.post<AuthResponse>('/auth/login', input);
      persist(res.data.accessToken, res.data.refreshToken, res.data.user);
      return res.data.user;
    },
    [persist],
  );

  const refresh = useCallback(async (): Promise<string> => {
    refreshRef.current = localStorage.getItem(STORAGE_REFRESH);
    const r = refreshRef.current;
    if (!r) throw new Error('Sin refresh token');

    const res = await apiClient.post<AuthResponse>('/auth/refresh', { refreshToken: r });
    persist(res.data.accessToken, res.data.refreshToken, res.data.user ?? user);
    return res.data.accessToken;
  }, [persist, user]);

  const logout = useCallback(() => clearSession(), [clearSession]);

  // Sincronizar entre pestañas: si en otra pestaña se cierra sesion, esta tambien.
  useEffect(() => {
    const onStorage = (e: StorageEvent) => {
      if (e.key === STORAGE_TOKEN || e.key === STORAGE_REFRESH || e.key === STORAGE_USER) {
        refreshRef.current = localStorage.getItem(STORAGE_REFRESH);
        setToken(localStorage.getItem(STORAGE_TOKEN));
        const raw = localStorage.getItem(STORAGE_USER);
        setUser(raw ? JSON.parse(raw) : null);
      }
    };
    window.addEventListener('storage', onStorage);
    return () => window.removeEventListener('storage', onStorage);
  }, []);

  const value = useMemo<AuthContextValue>(
    () => ({
      token,
      user,
      isAuthenticated: !!token && !!user,
      signup,
      login,
      logout,
      refresh,
      getRefreshToken: () => refreshRef.current,
    }),
    [token, user, signup, login, logout, refresh],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

// eslint-disable-next-line react-refresh/only-export-components
export function useLocalAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  // Tolerancia: si no hay Provider (p.ej. tests con mocks legacy), devolvemos
  // un fallback sin token para que useApi() caiga al flujo MSAL.
  if (!ctx) {
    return {
      token: null,
      user: null,
      isAuthenticated: false,
      signup: async () => {
        throw new Error('LocalAuthProvider no esta montado');
      },
      login: async () => {
        throw new Error('LocalAuthProvider no esta montado');
      },
      logout: () => {
        /* no-op */
      },
      refresh: async () => {
        throw new Error('LocalAuthProvider no esta montado');
      },
      getRefreshToken: () => null,
    };
  }
  return ctx;
}
